import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Upload, Send, CalendarClock, FileText, CheckCircle2, Circle, MessageSquare, AlertTriangle, BadgeCheck } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api } from "@/pages/portal/api";

const STAGES = [
  "interested", "application_submitted", "preliminarily_qualified", "future_season_partner",
  "documents_pending", "location_review", "insurance_review", "deployment_window_requested",
  "final_readiness_review", "vehicle_assigned", "training_scheduled", "ready_for_deployment", "live_partner",
];

const ACTION_PROMPTS = {
  confirm_window: "Confirm your season & location — is your requested season and waterfront still accurate? Update below or send a message.",
  update_docs: "90-day update required — please update your permits, insurance, staffing, and location details below.",
  begin_contracting: "Final contracting, system setup, and training scheduling are beginning — watch for document requests.",
  readiness_certification: "Final readiness certification required — complete the certification below.",
  final_authorization_window: "Final authorization window — Xtreme Exotics is reviewing your file for final deployment authorization.",
};

const pretty = (s) => (s || "").replace(/_/g, " ");

export default function PipelinePortal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [missing, setMissing] = useState(false);
  const [edit, setEdit] = useState({});
  const [msg, setMsg] = useState("");
  const [cert, setCert] = useState({ name: "", statement: "" });
  const fileRef = useRef(null);
  const [docLabel, setDocLabel] = useState("");

  const load = useCallback(() => {
    api.get("/pipeline/me")
      .then(({ data }) => { setP(data); setMissing(false); })
      .catch((e) => { if (e?.response?.status === 404) setMissing(true); else toast.error("Could not load pipeline"); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (fields) => {
    try { await api.put("/pipeline/me", fields); toast.success("Saved"); setEdit({}); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/pipeline/me/documents?label=${encodeURIComponent(docLabel || file.name)}`, fd);
      toast.success("Document uploaded");
      setDocLabel("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };

  const sendMsg = async () => {
    try { await api.post("/pipeline/me/messages", { message: msg }); setMsg(""); toast.success("Message sent"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
  };

  const certify = async () => {
    try { await api.post("/pipeline/me/certify-readiness", cert); toast.success("Readiness certified"); setCert({ name: "", statement: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Certification failed"); }
  };

  if (missing) {
    return (
      <div className="min-h-screen bg-ink" data-testid="pipeline-none">
        <Navbar />
        <main className="grid place-items-center px-5 py-40 text-center">
          <div className="max-w-md">
            <h1 className="font-display text-2xl font-extrabold text-offwhite">No pipeline profile yet</h1>
            <p className="mt-3 text-sm text-muted">This account ({user?.email}) isn't linked to a future-season pipeline. Apply first, or use the email you applied with.</p>
            <Link to="/pipeline/apply" className="btn-primary mt-6 inline-block" data-testid="pipeline-apply-link">Plan for a Future Season</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }
  if (!p) return <div className="grid min-h-screen place-items-center bg-ink"><Loader2 className="animate-spin text-aqua" /></div>;

  const stageIdx = STAGES.indexOf(p.status);
  const deadlines = [120, 90, 60, 30, 14]
    .map((d) => ({ d, date: p.earliest_deployment && new Date(new Date(p.earliest_deployment).getTime() - d * 864e5).toISOString().slice(0, 10), sent: p.reminders_sent.includes(d) }))
    .filter((x) => x.date);
  const missingItems = p.checklist.filter((c) => !c.done);
  const requestedOpen = (p.requested_documents || []).filter((r) => !r.fulfilled);

  return (
    <div className="min-h-screen bg-ink" data-testid="pipeline-portal">
      <Navbar />
      <div className="border-b border-black/[0.08] bg-white/80 pt-[92px] backdrop-blur">
        <div className="container-x py-5">
          <p className="eyebrow">Seasonal Partner Pipeline</p>
          <h1 className="font-display text-xl font-extrabold tracking-tight text-offwhite" data-testid="pipeline-title">{p.business_name}</h1>
          <p className="mt-1 text-xs text-muted">{p.pipeline_id} · {p.city_state}</p>
        </div>
      </div>

      <main className="container-x space-y-6 py-8 pb-24">
        <p className="rounded-lg border border-black/[0.08] bg-white p-3 text-[11px] leading-relaxed text-muted" data-testid="pipeline-notice">{p.no_guarantee_notice}</p>

        {p.pending_action && (
          <div className="flex items-start gap-3 rounded-xl border border-yellow-500/40 bg-yellow-400/10 p-4" data-testid="pipeline-action-banner">
            <AlertTriangle size={18} className="mt-0.5 shrink-0 text-yellow-700" />
            <p className="text-sm font-semibold text-yellow-800">{ACTION_PROMPTS[p.pending_action] || pretty(p.pending_action)}</p>
          </div>
        )}

        {/* stage tracker */}
        <div className="card-line rounded-xl bg-white p-6" data-testid="pipeline-stages">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-display text-base font-extrabold text-offwhite">Pipeline stage: <span className="text-aqua">{p.status_label}</span></h2>
            <span className="font-mono text-xs text-muted">Readiness {p.readiness_score}%</span>
          </div>
          <div className="mt-4 flex flex-wrap gap-1.5">
            {STAGES.map((s, i) => (
              <span key={s} className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
                i < stageIdx ? "bg-aqua/20 text-aqua" : i === stageIdx ? "bg-offwhite text-white" : "bg-black/[0.05] text-muted/60"}`}>
                {pretty(s)}
              </span>
            ))}
          </div>
          {(p.status === "deferred" || p.status === "declined") && (
            <p className="mt-3 text-sm font-semibold text-red-700">Status: {p.status_label} — contact your Xtreme Exotics representative with questions.</p>
          )}
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            {/* deployment window */}
            <div className="card-line rounded-xl bg-white p-6" data-testid="pipeline-window">
              <h2 className="flex items-center gap-2 font-display text-base font-extrabold text-offwhite"><CalendarClock size={15} className="text-aqua" /> Target deployment window</h2>
              <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
                <p><span className="text-muted">Season:</span> <b className="text-offwhite">{p.preferred_season}</b></p>
                <p><span className="text-muted">Operating months:</span> <b className="text-offwhite">{p.expected_months}</b></p>
                <p><span className="text-muted">Earliest:</span> <b className="text-offwhite">{p.earliest_deployment}</b> <span className="font-mono text-xs text-aqua">({p.days_to_earliest} days)</span></p>
                <p><span className="text-muted">Latest:</span> <b className="text-offwhite">{p.latest_deployment}</b></p>
                <p><span className="text-muted">Preferred Jet Car:</span> <b className="text-offwhite">{p.preferred_jet_car}</b> <span className="text-[10px] text-muted">(not guaranteed)</span></p>
                {p.tentative_vehicle_name && (
                  <p><span className="text-muted">Tentative vehicle:</span> <b className="text-offwhite">{p.tentative_vehicle_name}</b> <span className="text-[10px] text-muted">(tentative — not a reservation)</span></p>
                )}
              </div>
              <div className="mt-4 grid gap-3 border-t border-black/[0.06] pt-4 sm:grid-cols-4">
                {[["preferred_season", "Season"], ["earliest_deployment", "Earliest", "date"], ["latest_deployment", "Latest", "date"]].map(([k, label, type]) => (
                  <div key={k}>
                    <label className="field-label">{label}</label>
                    <input type={type || "text"} className="field !py-2 !text-xs" value={edit[k] ?? p[k] ?? ""} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} data-testid={`pipeline-edit-${k}`} />
                  </div>
                ))}
                <div className="flex items-end">
                  <button onClick={() => save({ preferred_season: edit.preferred_season ?? p.preferred_season, earliest_deployment: edit.earliest_deployment ?? p.earliest_deployment, latest_deployment: edit.latest_deployment ?? p.latest_deployment })}
                    className="btn-ghost w-full !py-2 !text-xs" data-testid="pipeline-save-window">Update window</button>
                </div>
              </div>
            </div>

            {/* readiness checklist */}
            <div className="card-line rounded-xl bg-white p-6" data-testid="pipeline-checklist">
              <h2 className="font-display text-base font-extrabold text-offwhite">Seasonal readiness checklist</h2>
              <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-black/[0.07]">
                <div className="h-full bg-aqua transition-[width] duration-500" style={{ width: `${p.readiness_score}%` }} />
              </div>
              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                {p.checklist.map((c) => (
                  <div key={c.key} className="flex items-start gap-2.5 text-xs" data-testid={`checklist-${c.key}`}>
                    {c.done ? <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-emerald-600" /> : <Circle size={15} className="mt-0.5 shrink-0 text-black/20" />}
                    <div>
                      <span className={c.done ? "font-semibold text-offwhite" : "text-muted"}>{c.label}</span>
                      {c.done && c.at && <p className="font-mono text-[10px] text-muted">verified {c.at.slice(0, 10)}</p>}
                    </div>
                  </div>
                ))}
              </div>
              {missingItems.length > 0 && (
                <p className="mt-4 rounded-lg bg-surface/60 p-3 text-xs text-muted" data-testid="pipeline-missing">
                  Missing requirements: {missingItems.map((c) => c.label).join(" · ")}
                </p>
              )}
            </div>

            {/* readiness certification */}
            {!p.certification ? (
              <div className="card-line rounded-xl bg-white p-6" data-testid="pipeline-certify">
                <h2 className="flex items-center gap-2 font-display text-base font-extrabold text-offwhite"><BadgeCheck size={15} className="text-aqua" /> Final readiness certification</h2>
                <p className="mt-1 text-xs text-muted">Required ~30 days before deployment. Certify that your operation is fully ready.</p>
                <div className="mt-4 grid gap-3">
                  <input className="field !py-2.5 !text-sm" placeholder="Your full legal name" value={cert.name} onChange={(e) => setCert({ ...cert, name: e.target.value })} data-testid="cert-name" />
                  <textarea className="field min-h-[70px] !text-sm" placeholder="Certification statement (e.g. I certify that Clearwater Jet Adventures is fully ready…)" value={cert.statement} onChange={(e) => setCert({ ...cert, statement: e.target.value })} data-testid="cert-statement" />
                  <button onClick={certify} disabled={cert.name.trim().length < 2 || cert.statement.trim().length < 10} className="btn-ghost !py-2.5 !text-xs disabled:opacity-40" data-testid="cert-submit-btn">Certify Readiness</button>
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-400/[0.07] p-5 text-sm text-emerald-800" data-testid="pipeline-certified">
                Certified by {p.certification.name} on {p.certification.certified_at.slice(0, 10)}
              </div>
            )}

            {/* messages */}
            <div className="card-line rounded-xl bg-white p-6" data-testid="pipeline-messages">
              <h2 className="flex items-center gap-2 font-display text-base font-extrabold text-offwhite"><MessageSquare size={15} className="text-aqua" /> Messages & support</h2>
              <div className="mt-4 max-h-64 space-y-2 overflow-y-auto">
                {!(p.messages || []).length && <p className="text-xs text-muted">No messages yet.</p>}
                {(p.messages || []).map((m) => (
                  <div key={m.message_id} className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm ${m.from === "partner" ? "ml-auto bg-offwhite text-white" : "bg-surface text-offwhite"}`}>
                    <p>{m.message}</p>
                    <p className="mt-1 font-mono text-[9px] opacity-60">{m.from === "partner" ? "You" : "Xtreme Exotics"} · {new Date(m.at).toLocaleString()}</p>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex gap-2">
                <input className="field !py-2.5 !text-sm" placeholder="Write to your XE representative…" value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="pipeline-msg-input" />
                <button onClick={sendMsg} disabled={msg.trim().length < 2} className="btn-primary !px-4 !py-2 !text-xs disabled:opacity-40" data-testid="pipeline-msg-send"><Send size={13} /></button>
              </div>
            </div>
          </div>

          {/* right column */}
          <div className="space-y-6">
            <div className="card-line rounded-xl bg-white p-5" data-testid="pipeline-rep">
              <h2 className="font-display text-sm font-extrabold text-offwhite">Your XE representative</h2>
              <p className="mt-2 text-sm text-muted">{p.assigned_rep || "Assigned after preliminary qualification"}</p>
              <p className="mt-1 text-xs text-muted">Training status: <b className="text-offwhite">{p.checklist.find((c) => c.key === "training_completed")?.done ? "Completed" : "Pending"}</b></p>
            </div>

            <div className="rounded-xl border border-aqua/30 bg-aqua/[0.06] p-5" data-testid="pipeline-insurance-covered">
              <h2 className="font-display text-sm font-extrabold text-offwhite">Insurance — covered by Xtreme Exotics</h2>
              <p className="mt-2 text-xs leading-relaxed text-muted">
                Your Jet Car operates under Xtreme Exotics' commercial watercraft/rental liability and
                physical-damage coverage (XINSURANCE fleet-partnership agreements), and you are added as an
                additional insured. You contribute 45% of any deductible, capped at $2,500 — Xtreme Exotics
                covers the balance.
              </p>
            </div>

            <div className="card-line rounded-xl bg-white p-5" data-testid="pipeline-deadlines">
              <h2 className="font-display text-sm font-extrabold text-offwhite">Upcoming deadlines</h2>
              <div className="mt-3 space-y-2">
                {deadlines.map((d) => (
                  <div key={d.d} className="flex items-center justify-between text-xs" data-testid={`deadline-${d.d}`}>
                    <span className="text-muted">{d.d}-day reminder</span>
                    <span className={`font-mono ${d.sent ? "text-emerald-700" : "text-offwhite"}`}>{d.sent ? "sent" : d.date}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="card-line rounded-xl bg-white p-5" data-testid="pipeline-documents">
              <h2 className="flex items-center gap-2 font-display text-sm font-extrabold text-offwhite"><FileText size={14} className="text-aqua" /> Documents</h2>
              {requestedOpen.length > 0 && (
                <div className="mt-3 rounded-lg border border-yellow-500/40 bg-yellow-400/10 p-3" data-testid="pipeline-doc-requests">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-yellow-800">Requested by XE</p>
                  <ul className="mt-1 list-inside list-disc text-xs text-yellow-800">
                    {requestedOpen.flatMap((r) => r.items).map((i) => <li key={i}>{i}</li>)}
                  </ul>
                </div>
              )}
              <div className="mt-3 space-y-1.5">
                {!(p.documents || []).length && <p className="text-xs text-muted">No documents uploaded yet.</p>}
                {(p.documents || []).map((d) => (
                  <p key={d.doc_id} className="flex justify-between gap-2 text-xs" data-testid={`pipeline-doc-${d.doc_id}`}>
                    <span className="font-semibold text-offwhite">{d.label}</span>
                    <span className="font-mono text-[10px] text-muted">{d.at.slice(0, 10)}</span>
                  </p>
                ))}
              </div>
              <div className="mt-3 space-y-2 border-t border-black/[0.06] pt-3">
                <input className="field !py-2 !text-xs" placeholder="Document label (e.g. Insurance COI)" value={docLabel} onChange={(e) => setDocLabel(e.target.value)} data-testid="pipeline-doc-label" />
                <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.txt" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="pipeline-doc-file" />
                <button onClick={() => fileRef.current?.click()} className="btn-ghost w-full !py-2 !text-xs" data-testid="pipeline-upload-btn"><Upload size={13} /> Upload document</button>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
